<template>
  <section class="pro-dashboard-page">
    <div class="dashboard-shell">
      <QuickStatsLoadingSkeleton
        v-if="isDashboardLoading"
        class="dashboard-loading-skeleton"
        label="Loading pro dashboard"
        :card-count="3"
        :row-count="3"
      />

      <header v-else-if="session" class="dashboard-hero">
        <div class="dashboard-hero-main">
          <p class="eyebrow">Pro Dashboard</p>
          <h1>Welcome, {{ displayUsername }}!</h1>
          <div class="hero-lock-note">
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M7 10V7a5 5 0 0 1 10 0v3" />
              <rect width="14" height="11" x="5" y="10" rx="2" />
            </svg>
            <p>{{ premiumStatusLabel }}</p>
          </div>
          <div class="membership-cta">
            <template v-if="isPremiumUser">
              <p>Premium is active. Full dashboard access is ready when private tools go live.</p>
            </template>
            <template v-else-if="isProUser">
              <p>Pro access is active. Upgrade to premium through the mobile app to unlock the full dashboard.</p>
            </template>
            <template v-else>
              <p>Claim your 7-day free trial and save 17% with a web subscription.</p>
              <button type="button" class="trial-cta-button" @click="openSettingsModal">
                Claim 7-Day Free Trial
              </button>
            </template>
          </div>
        </div>
        <div class="dashboard-hero-actions">
          <button type="button" class="settings-button" aria-label="Settings" title="Settings" @click="openSettingsModal">
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6V20a2 2 0 0 1-4 0v-.1a1.65 1.65 0 0 0-1-.6 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-.6-1H4a2 2 0 0 1 0-4h.1a1.65 1.65 0 0 0 .6-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-.6V4a2 2 0 0 1 4 0v.1a1.65 1.65 0 0 0 1 .6 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.36.35.7.6 1h.1a2 2 0 0 1 0 4H20a1.65 1.65 0 0 0-.6 1Z" />
            </svg>
          </button>
          <a :href="IOS_APP_STORE_URL" target="_blank" rel="noopener noreferrer" class="ios-download-button">
            Download iOS App
          </a>
        </div>
      </header>

      <div
        v-if="!isDashboardLoading && session && billingBannerMessage"
        class="billing-banner"
        :class="{ error: billingError }"
        role="status"
      >
        {{ billingBannerMessage }}
      </div>

      <div v-if="!isDashboardLoading && !session" class="auth-layout auth-layout--simple">
        <div class="auth-page-header">
          <h1>Ready to Bet Smart?</h1>
          <p>Create an account or login now.</p>
        </div>

        <form class="auth-panel auth-card" @submit.prevent="goToAuthPath">
          <div class="panel-header">
            <p class="eyebrow">{{ authContent.eyebrow }}</p>
            <h2 class="auth-card-title">{{ authContent.title }}</h2>
          </div>

          <div class="form-group auth-phone-group">
            <label for="phone" class="form-label">Phone Number</label>
            <div class="phone-input-wrap" :class="{ error: authError }">
              <span class="country-prefix" aria-hidden="true">+1</span>
              <input
                id="phone"
                v-model="phone"
                type="tel"
                inputmode="numeric"
                autocomplete="tel-national"
                class="phone-input"
                placeholder="555 123 4567"
                maxlength="14"
                required
                aria-describedby="phone-help"
                @input="formatPhoneInput"
              />
            </div>
            <p id="phone-help" class="form-help">
              Paulie is currently available only in the U.S. and Canada.
            </p>
            <span v-if="authError" class="error-message">{{ authError }}</span>
          </div>

          <div class="auth-actions auth-actions--centered">
            <button type="submit" class="btn btn-primary">{{ authContent.cta }}</button>
          </div>
          <p class="auth-login-note">
            {{ authContent.switchPrompt }}
            <button type="button" class="auth-context-link" @click="setAuthMode(authContent.switchMode)">
              {{ authContent.switchLabel }}
            </button>
          </p>
        </form>
      </div>

      <div v-else-if="!isDashboardLoading && needsProfile" class="auth-layout auth-layout--simple">
        <div class="auth-page-header">
          <h1>Complete Your Account</h1>
          <p>Choose a username before opening the dashboard.</p>
        </div>

        <form class="auth-panel auth-card" @submit.prevent="saveProfile">
          <p class="form-detail">
            This finishes your web account setup. Your username can be used across Paulie account features.
          </p>

          <div class="form-group auth-phone-group">
            <label for="profileUsername" class="form-label">Username</label>
            <input
              id="profileUsername"
              v-model="profileForm.username"
              type="text"
              class="form-input"
              :class="{ error: profileErrors.username }"
              placeholder="Choose a username"
              autocomplete="username"
              maxlength="24"
              required
              @input="formatProfileUsername"
            />
            <p class="form-help">Use 3-24 letters, numbers, or underscores.</p>
            <span v-if="profileErrors.username" class="error-message">{{ profileErrors.username }}</span>
          </div>

          <p class="auth-login-note join-code-toggle">
            {{ showProfileJoinCode ? 'No join code?' : 'Have a join code?' }}
            <button type="button" class="auth-context-link" @click="toggleProfileJoinCode">
              {{ showProfileJoinCode ? 'Remove it' : 'Add it' }}
            </button>
          </p>

          <div v-if="showProfileJoinCode" class="form-group auth-phone-group join-code-panel">
            <label for="profileJoinCode" class="form-label">Join Code <span>Optional</span></label>
            <input
              id="profileJoinCode"
              v-model="profileForm.joinCode"
              type="text"
              class="form-input"
              :class="{ error: profileErrors.joinCode }"
              placeholder="Enter code"
              autocomplete="off"
              maxlength="32"
              @input="formatProfileJoinCode"
            />
            <span v-if="profileErrors.joinCode" class="error-message">{{ profileErrors.joinCode }}</span>
          </div>

          <button type="submit" class="btn btn-primary w-full" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            {{ loading ? 'Saving...' : 'Continue' }}
          </button>
        </form>
      </div>

      <div v-else-if="!isDashboardLoading" class="dashboard-content">
        <div v-if="!isPremiumUser" class="locked-dashboard-mark" aria-label="Premium dashboard locked">
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M7 10V7a5 5 0 0 1 10 0v3" />
            <rect width="14" height="11" x="5" y="10" rx="2" />
          </svg>
        </div>

        <section v-else class="tool-panel">
          <div>
            <p class="eyebrow">Coming Next</p>
            <h2>Account Tools</h2>
            <p>
              The authentication layer is live here. We can now attach private account tools and data
              to this dashboard without changing the rest of the site.
            </p>
          </div>
        </section>
      </div>
    </div>

    <div
      v-if="settingsModalOpen"
      class="settings-modal-backdrop"
      role="presentation"
      @click.self="closeSettingsModal"
    >
      <section class="settings-modal" role="dialog" aria-modal="true" aria-labelledby="settingsTitle">
        <header class="settings-modal-header">
          <div>
            <p class="eyebrow">Settings</p>
            <h2 id="settingsTitle">Account Settings</h2>
          </div>
          <button type="button" class="modal-close-button" aria-label="Close settings" @click="closeSettingsModal">
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </header>

        <form class="settings-form" @submit.prevent="saveSettingsProfile">
          <div class="form-group">
            <label for="settingsUsername" class="form-label">Username</label>
            <div class="settings-username-row">
              <input
                id="settingsUsername"
                ref="settingsUsernameInput"
                v-model="settingsForm.username"
                type="text"
                class="form-input"
                :class="{ error: settingsErrors.username, locked: !settingsUsernameEditing }"
                placeholder="Choose a username"
                autocomplete="username"
                maxlength="24"
                required
                :readonly="!settingsUsernameEditing"
                @input="formatSettingsUsername"
              />
              <div class="settings-username-actions">
                <button
                  type="button"
                  class="settings-inline-button"
                  :disabled="settingsSaving"
                  @click="enableSettingsUsernameEdit"
                >
                  {{ settingsUsernameEditing ? 'Editing' : 'Edit' }}
                </button>
                <button
                  type="submit"
                  class="settings-inline-button settings-inline-button-primary"
                  :disabled="!settingsCanSaveUsername"
                >
                  {{ settingsSaving ? 'Saving...' : 'Save' }}
                </button>
              </div>
            </div>
            <p class="form-help">Use 3-24 letters, numbers, or underscores.</p>
            <span v-if="settingsErrors.username" class="error-message">{{ settingsErrors.username }}</span>
          </div>

          <div class="settings-tier-card">
            <span class="settings-tier-label">Billing Tier</span>
            <strong>{{ billingTierLabel }}</strong>
            <p>{{ billingTierDescription }}</p>
          </div>

          <div v-if="isFreeUser" class="settings-upgrade-card">
            <p>Start your 7-day free trial and save 17% with a web subscription.</p>
            <button type="button" class="trial-cta-button" :disabled="checkoutLoading" @click="startSubscriptionCheckout">
              <span v-if="checkoutLoading" class="spinner"></span>
              {{ checkoutLoading ? 'Loading...' : 'Subscribe Now' }}
            </button>
          </div>

          <div v-else-if="isProUser" class="settings-upgrade-card">
            <p>Upgrade to premium through the mobile app to unlock full dashboard access.</p>
            <a :href="IOS_APP_STORE_URL" target="_blank" rel="noopener noreferrer" class="ios-download-button">
              Download iOS App
            </a>
          </div>

          <div v-else-if="isPremiumUser" class="settings-upgrade-card">
            <p>{{ premiumSubscriptionDescription }}</p>
            <button
              v-if="canCancelSubscription"
              type="button"
              class="settings-inline-button settings-inline-button-danger"
              :disabled="cancelLoading"
              @click="cancelPremiumSubscription"
            >
              {{ cancelLoading ? 'Cancelling...' : 'Cancel Subscription' }}
            </button>
          </div>

          <p v-if="settingsMessage" class="settings-message">{{ settingsMessage }}</p>

          <div class="settings-modal-actions">
            <button type="button" class="btn btn-primary" @click="closeSettingsModal">Done</button>
          </div>
        </form>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import QuickStatsLoadingSkeleton from '@/components/QuickStatsLoadingSkeleton.vue'
import { IOS_APP_STORE_URL } from '@/lib/appLinks'
import { billingService } from '@/lib/billingService'
import { auth, userProfiles } from '@/lib/supabase'

const router = useRouter()
const route = useRoute()

const initializing = ref(true)
const loading = ref(false)
const authMode = ref('register')
const showProfileJoinCode = ref(false)
const checkoutLoading = ref(false)
const cancelLoading = ref(false)
const billingStatus = ref(null)
const billingNotice = ref('')
const billingError = ref('')
const settingsModalOpen = ref(false)
const settingsUsernameEditing = ref(false)
const settingsSaving = ref(false)
const settingsMessage = ref('')
const originalSettingsUsername = ref('')
const settingsUsernameInput = ref(null)
const profileLoading = ref(false)
const phone = ref('')
const authError = ref('')
const session = ref(null)
const user = ref(null)
const accountProfile = ref(null)
const profileErrors = reactive({})
const settingsErrors = reactive({})
const profileForm = reactive({
  username: '',
  joinCode: ''
})
const settingsForm = reactive({
  username: ''
})
let authSubscription = null

const phoneDigits = computed(() => phone.value.replace(/\D/g, '').slice(0, 10))
const normalizedPhone = computed(() => (phoneDigits.value.length === 10 ? `+1${phoneDigits.value}` : ''))
const userMetadata = computed(() => user.value?.user_metadata || {})
const isDashboardLoading = computed(() => initializing.value)
const hasUsername = computed(() => /^[a-z0-9_]{3,24}$/.test(String(accountProfile.value?.username || '')))
const needsProfile = computed(() => Boolean(session.value && !profileLoading.value && !hasUsername.value))
const displayUsername = computed(() => accountProfile.value?.username || userMetadata.value.username || 'there')
const normalizedTier = computed(() => String(billingStatus.value?.tier || accountProfile.value?.tier || 'free').trim().toLowerCase())
const isProUser = computed(() => normalizedTier.value === 'pro')
const isPremiumUser = computed(() => {
  if (billingStatus.value) {
    return billingStatus.value.is_subscribed === true && normalizedTier.value === 'premium'
  }

  return accountProfile.value?.is_subscribed === true && normalizedTier.value === 'premium'
})
const isFreeUser = computed(() => normalizedTier.value === 'free' && !isPremiumUser.value)
const billingBannerMessage = computed(() => billingError.value || billingNotice.value)
const billingTierLabel = computed(() => {
  if (isPremiumUser.value) return 'Premium'
  if (isProUser.value) return 'Pro'
  return 'Free'
})
const billingTierDescription = computed(() => {
  if (isPremiumUser.value) return 'Premium membership is active for this account.'
  if (isProUser.value) return 'Pro access is active. Premium unlocks the full dashboard experience.'
  return 'Free account. Premium membership is required for dashboard access.'
})
const settingsUsernameChanged = computed(() => settingsForm.username !== originalSettingsUsername.value)
const settingsCanSaveUsername = computed(() => (
  settingsUsernameEditing.value &&
  settingsUsernameChanged.value &&
  !settingsSaving.value
))
const canCancelSubscription = computed(() => isPremiumUser.value && billingStatus.value?.cancel_at_period_end !== true)
const premiumSubscriptionDescription = computed(() => {
  if (billingStatus.value?.cancel_at_period_end) {
    return `Premium stays active until ${formatBillingDate(billingStatus.value.current_period_end)}.`
  }

  if (billingStatus.value?.trial_end) {
    return `Premium trial is active until ${formatBillingDate(billingStatus.value.trial_end)}.`
  }

  if (billingStatus.value?.current_period_end) {
    return `Premium renews on ${formatBillingDate(billingStatus.value.current_period_end)}.`
  }

  return 'Premium membership is active for this account.'
})
const premiumStatusLabel = computed(() => {
  if (profileLoading.value) return 'Checking premium access status.'
  if (isPremiumUser.value) return 'Dashboard unlocked with premium membership.'
  return 'Dashboard requires premium membership.'
})

const authContent = computed(() => {
  if (authMode.value === 'login') {
    return {
      eyebrow: 'Welcome Back',
      title: 'Login to Your Account',
      cta: 'Login',
      switchPrompt: 'Need an account?',
      switchLabel: 'Register',
      switchMode: 'register'
    }
  }

  return {
    eyebrow: 'Get Started',
    title: 'Create Your Account',
    cta: 'Register',
    switchPrompt: 'Already have an account?',
    switchLabel: 'Login',
    switchMode: 'login'
  }
})

const validatePhone = () => {
  authError.value = ''

  if (!phoneDigits.value) {
    authError.value = 'Phone number is required.'
  } else if (phoneDigits.value.length !== 10) {
    authError.value = 'Enter a 10-digit U.S. or Canada phone number.'
  } else if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(phoneDigits.value)) {
    authError.value = 'Enter a valid U.S. or Canada phone number.'
  }

  return !authError.value
}

const formatPhoneInput = () => {
  const digits = phoneDigits.value

  if (digits.length <= 3) {
    phone.value = digits
  } else if (digits.length <= 6) {
    phone.value = `${digits.slice(0, 3)} ${digits.slice(3)}`
  } else {
    phone.value = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
  }
}

const syncProfileForm = (currentUser, profile = accountProfile.value) => {
  const metadata = currentUser?.user_metadata || {}
  profileForm.username = profile?.username || metadata.username || ''
  profileForm.joinCode = profile ? profile.influencer_code || '' : metadata.join_code || ''
  showProfileJoinCode.value = Boolean(profile ? profile.influencer_code : metadata.join_code)
}

const setAuthMode = (mode) => {
  authMode.value = mode
  authError.value = ''
}

const formatProfileUsername = () => {
  profileForm.username = profileForm.username.toLowerCase().replace(/[^a-z0-9_]/g, '')
  profileErrors.username = ''
}

const formatSettingsUsername = () => {
  settingsForm.username = settingsForm.username.toLowerCase().replace(/[^a-z0-9_]/g, '')
  settingsErrors.username = ''
  settingsMessage.value = ''
}

const formatProfileJoinCode = () => {
  profileForm.joinCode = profileForm.joinCode.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '')
  profileErrors.joinCode = ''
}

const toggleProfileJoinCode = () => {
  showProfileJoinCode.value = !showProfileJoinCode.value

  if (!showProfileJoinCode.value) {
    profileForm.joinCode = ''
    profileErrors.joinCode = ''
  }
}

const validateProfile = () => {
  profileErrors.username = ''
  profileErrors.joinCode = ''

  if (!profileForm.username.trim()) {
    profileErrors.username = 'Username is required.'
  } else if (!/^[a-z0-9_]{3,24}$/.test(profileForm.username)) {
    profileErrors.username = 'Use 3-24 letters, numbers, or underscores.'
  }

  if (showProfileJoinCode.value && profileForm.joinCode && !/^[A-Z0-9_-]{3,32}$/.test(profileForm.joinCode)) {
    profileErrors.joinCode = 'Join codes can use letters, numbers, hyphens, or underscores.'
  }

  return !profileErrors.username && !profileErrors.joinCode
}

const validateSettingsProfile = () => {
  settingsErrors.username = ''

  if (!settingsForm.username.trim()) {
    settingsErrors.username = 'Username is required.'
  } else if (!/^[a-z0-9_]{3,24}$/.test(settingsForm.username)) {
    settingsErrors.username = 'Use 3-24 letters, numbers, or underscores.'
  }

  return !settingsErrors.username
}

const saveProfile = async () => {
  if (!validateProfile()) return

  loading.value = true

  try {
    const joinCode = showProfileJoinCode.value && profileForm.joinCode ? profileForm.joinCode : ''
    const { data, error } = await userProfiles.complete({
      userId: user.value?.id,
      phoneNumber: user.value?.phone || '',
      username: profileForm.username,
      joinCode
    })

    if (error) {
      throw error
    }

    accountProfile.value = data
    syncProfileForm(user.value, data)

    try {
      await auth.updateUserData({
        ...userMetadata.value,
        username: profileForm.username,
        ...(joinCode ? { join_code: joinCode } : {})
      })
    } catch (metadataError) {
      console.warn('Non-fatal metadata sync error:', metadataError)
    }
  } catch (error) {
    console.error('Profile completion error:', error)
    profileErrors.username = formatAuthError(error, 'We could not save your account details.')
  } finally {
    loading.value = false
  }
}

const openSettingsModal = () => {
  const currentUsername = accountProfile.value?.username || userMetadata.value.username || ''
  originalSettingsUsername.value = currentUsername
  settingsForm.username = currentUsername
  settingsUsernameEditing.value = false
  settingsErrors.username = ''
  settingsMessage.value = ''
  settingsModalOpen.value = true
}

const closeSettingsModal = () => {
  if (settingsSaving.value) return
  settingsModalOpen.value = false
  settingsUsernameEditing.value = false
  settingsErrors.username = ''
  settingsMessage.value = ''
}

const enableSettingsUsernameEdit = async () => {
  if (settingsSaving.value) return
  settingsUsernameEditing.value = true
  settingsMessage.value = ''
  await nextTick()
  settingsUsernameInput.value?.focus()
  settingsUsernameInput.value?.select()
}

const saveSettingsProfile = async () => {
  if (!settingsUsernameEditing.value || !settingsUsernameChanged.value) return
  if (!validateSettingsProfile()) return

  settingsSaving.value = true
  settingsMessage.value = ''

  try {
    const joinCode = accountProfile.value?.influencer_code || ''
    const { data, error } = await userProfiles.complete({
      userId: user.value?.id,
      phoneNumber: user.value?.phone || accountProfile.value?.phone_number || '',
      username: settingsForm.username,
      joinCode
    })

    if (error) {
      throw error
    }

    accountProfile.value = data
    syncProfileForm(user.value, data)
    originalSettingsUsername.value = settingsForm.username
    settingsUsernameEditing.value = false

    try {
      await auth.updateUserData({
        ...userMetadata.value,
        username: settingsForm.username,
        ...(joinCode ? { join_code: joinCode } : {})
      })
    } catch (metadataError) {
      console.warn('Non-fatal metadata sync error:', metadataError)
    }

    settingsMessage.value = 'Username updated.'
  } catch (error) {
    console.error('Settings update error:', error)
    settingsErrors.username = formatAuthError(error, 'We could not save your settings.')
  } finally {
    settingsSaving.value = false
  }
}

const formatBillingDate = (value) => {
  if (!value) return 'the end of the billing period'
  const timestamp = Number(value)
  const date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp)

  if (Number.isNaN(date.getTime())) return 'the end of the billing period'

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

const clearBillingQuery = async () => {
  const nextQuery = { ...route.query }
  delete nextQuery.billing

  await router.replace({
    path: route.path,
    query: nextQuery
  })
}

const handleBillingReturn = async () => {
  const billingResult = route.query.billing
  if (!billingResult) return

  if (billingResult === 'success') {
    billingNotice.value = 'Subscription checkout complete. Refreshing access...'
    billingError.value = ''
    await loadAccountProfile(user.value)
    billingNotice.value = 'Subscription status refreshed.'
  } else if (billingResult === 'cancel') {
    billingNotice.value = 'Checkout canceled. No changes were made.'
    billingError.value = ''
  }

  await clearBillingQuery()
}

const startSubscriptionCheckout = async () => {
  checkoutLoading.value = true
  billingError.value = ''
  billingNotice.value = ''
  settingsMessage.value = 'Loading checkout...'

  try {
    const origin = window.location.origin
    const data = await billingService.startCheckout({
      successUrl: `${origin}/pro-dashboard?billing=success`,
      cancelUrl: `${origin}/pro-dashboard?billing=cancel`,
      includeTrial: true
    })

    const checkoutUrl = data?.url || data?.checkout_url

    if (!checkoutUrl) {
      throw new Error('Stripe did not return a checkout URL. Please try again.')
    }

    window.location.assign(checkoutUrl)
  } catch (error) {
    console.error('Checkout start error:', error)
    billingError.value = error.message || 'We could not open Stripe checkout.'
    settingsMessage.value = billingError.value
  } finally {
    checkoutLoading.value = false
  }
}

const cancelPremiumSubscription = async () => {
  const confirmed = window.confirm('Cancel premium at the end of the current billing period?')
  if (!confirmed) return

  cancelLoading.value = true
  billingError.value = ''
  billingNotice.value = ''
  settingsMessage.value = 'Updating subscription...'

  try {
    const result = await billingService.cancelSubscription()
    billingStatus.value = {
      ...(billingStatus.value || {}),
      cancel_at_period_end: result.cancel_at_period_end,
      current_period_end: result.current_period_end,
      is_subscribed: true,
      tier: 'premium'
    }
    billingNotice.value = `Subscription will stay active until ${formatBillingDate(result.current_period_end)}.`
    settingsMessage.value = billingNotice.value
  } catch (error) {
    console.error('Subscription cancel error:', error)
    billingError.value = error.message || 'We could not cancel your subscription.'
    settingsMessage.value = billingError.value
  } finally {
    cancelLoading.value = false
  }
}

const goToAuthPath = () => {
  if (!validatePhone()) return

  router.push({
    path: authMode.value === 'login' ? '/login' : '/register',
    query: { phone: normalizedPhone.value }
  })
}

const loadAccountProfile = async (currentUser) => {
  if (!currentUser?.id) {
    accountProfile.value = null
    profileLoading.value = false
    syncProfileForm(null, null)
    return
  }

  profileLoading.value = true

  try {
    const { data, error } = await userProfiles.fetch(currentUser.id)

    if (error) {
      throw error
    }

    accountProfile.value = data
    syncProfileForm(currentUser, data)
  } catch (error) {
    console.error('Account profile check error:', error)
    accountProfile.value = null
    syncProfileForm(currentUser, null)
  } finally {
    profileLoading.value = false
  }
}

onMounted(async () => {
  try {
    const { session: currentSession } = await auth.getSession()
    session.value = currentSession
    user.value = currentSession?.user || null
    await loadAccountProfile(user.value)
    if (currentSession) {
      await handleBillingReturn()
    }

    const { data } = auth.onAuthStateChange(async (_event, currentSession) => {
      session.value = currentSession
      user.value = currentSession?.user || null
      await loadAccountProfile(user.value)
      billingStatus.value = null
    })

    authSubscription = data?.subscription
  } catch (error) {
    console.error('Pro dashboard initialization error:', error)
    authError.value = 'We could not initialize authentication. Please refresh the page.'
  } finally {
    initializing.value = false
  }
})

onUnmounted(() => {
  authSubscription?.unsubscribe()
})

const formatAuthError = (error, fallback) => {
  const message = error?.message || ''

  if (error?.code === '23505' || message.toLowerCase().includes('duplicate key')) {
    return 'That username is already taken.'
  }

  if (!message) return fallback
  return `${fallback} Supabase returned: ${message}`
}
</script>

<style scoped>
.pro-dashboard-page {
  min-height: calc(100vh - 220px);
  padding: clamp(1rem, 3vw, 2.5rem) 1rem 3rem;
}

.dashboard-shell {
  width: min(1120px, 100%);
  margin: 0 auto;
}

.dashboard-hero,
.tool-panel,
.auth-panel {
  background: var(--bg-card);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

.dashboard-hero {
  border-radius: 10px;
  padding: clamp(1.1rem, 3vw, 1.6rem);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.billing-banner {
  margin-bottom: 1rem;
  border: 1px solid rgba(91, 124, 255, 0.22);
  border-radius: 10px;
  padding: 0.78rem 0.95rem;
  background: rgba(91, 124, 255, 0.08);
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 800;
}

.billing-banner.error {
  border-color: rgba(239, 68, 68, 0.34);
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
}

.dashboard-hero-main {
  flex: 1 1 auto;
}

.hero-lock-note {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 0.2rem;
}

.hero-lock-note svg {
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
  fill: none;
  stroke: var(--text-primary);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.hero-lock-note p {
  color: var(--text-primary);
  font-weight: 800;
}

.membership-cta {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.membership-cta p {
  max-width: 54ch;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.trial-cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  border: 0;
  border-radius: 10px;
  padding: 0.72rem 1.05rem;
  background: var(--accent);
  color: #ffffff;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(91, 124, 255, 0.24);
  transition: background var(--t-fast), box-shadow var(--t-fast), transform var(--t-fast);
}

.trial-cta-button:hover {
  transform: translateY(-1px);
  background: #3654ff;
  box-shadow: 0 14px 34px rgba(91, 124, 255, 0.32);
}

.trial-cta-button:disabled {
  cursor: not-allowed;
  opacity: 0.68;
  transform: none;
}

.dashboard-hero-actions {
  flex: 0 0 auto;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.7rem;
}

.settings-button {
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #ffffff;
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color var(--t-fast), box-shadow var(--t-fast), transform var(--t-fast), background var(--t-fast);
}

.settings-button:hover {
  transform: translateY(-1px);
  border-color: var(--border-strong);
  background: #f7f7f2;
  box-shadow: var(--shadow-sm);
}

.settings-button svg {
  width: 19px;
  height: 19px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.ios-download-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  border-radius: 10px;
  padding: 0.7rem 0.95rem;
  background: #080808;
  color: #ffffff;
  font-size: 0.84rem;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 12px 26px rgba(8, 8, 8, 0.16);
  transition: background var(--t-fast), box-shadow var(--t-fast), transform var(--t-fast);
}

.ios-download-button:hover {
  transform: translateY(-1px);
  background: #1b1b1b;
  box-shadow: 0 14px 32px rgba(8, 8, 8, 0.22);
}

.dashboard-hero h1,
.panel-header h2,
.tool-panel h2 {
  font-size: clamp(1.7rem, 4vw, 2.4rem);
  margin-bottom: 0.5rem;
}

.panel-header p,
.tool-panel p,
.membership-cta p {
  max-width: 68ch;
}

.eyebrow {
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 0.35rem;
}

.auth-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 1rem;
  align-items: stretch;
}

.auth-layout--simple {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: min(620px, calc(100vh - 220px));
}

.auth-page-header {
  width: min(520px, 100%);
  text-align: center;
  margin: 0 auto 0.2rem;
}

.auth-page-header h1 {
  font-size: clamp(2rem, 5vw, 3rem);
  line-height: 1.04;
  margin-bottom: 0.6rem;
  letter-spacing: -0.035em;
  color: var(--text-primary);
}

.auth-page-header p {
  color: var(--text-secondary);
  font-size: 1rem;
}

.auth-panel,
.tool-panel {
  border-radius: 10px;
  padding: clamp(1rem, 3vw, 1.5rem);
}

.auth-card {
  width: min(400px, 100%);
  text-align: center;
  padding: clamp(1.25rem, 3vw, 1.75rem);
}

.form-detail {
  margin-bottom: 1rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.45;
  text-align: center;
}

.auth-card-title {
  font-size: 0.9rem;
  line-height: 1.25;
}

.panel-header {
  margin-bottom: 1.25rem;
}

.auth-phone-group {
  text-align: left;
  margin-bottom: 1rem;
}

.form-label span {
  color: var(--text-muted);
  font-size: 0.78rem;
  font-weight: 700;
}

.form-input.error {
  border-color: rgba(252, 165, 165, 0.82);
}

.phone-input-wrap {
  display: flex;
  align-items: center;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: #fff;
  transition: border-color var(--t-fast), box-shadow var(--t-fast), background var(--t-fast);
}

.phone-input-wrap:focus-within {
  border-color: rgba(91, 124, 255, 0.8);
  box-shadow: 0 0 0 3px rgba(91, 124, 255, 0.18);
  background: #fff;
}

.phone-input-wrap.error {
  border-color: rgba(252, 165, 165, 0.82);
}

.country-prefix {
  flex: 0 0 auto;
  color: var(--accent);
  font-weight: 800;
  padding: 0 0.85rem;
  border-right: 1px solid var(--border);
}

.phone-input {
  min-width: 0;
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--text-primary);
  padding: 0.75rem 0.85rem;
  font-size: 0.95rem;
}

.phone-input:focus {
  outline: none;
}

.form-help {
  margin-top: 0.4rem;
  color: var(--text-muted);
  font-size: 0.84rem;
}

.code-input {
  font-size: 1.25rem;
  letter-spacing: 0.12em;
}

.auth-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.auth-actions--centered {
  justify-content: center;
}

.auth-actions .btn {
  flex: 1 1 160px;
  min-height: 44px;
}

.auth-card .btn {
  flex: 1 1 100%;
}

.auth-login-note {
  margin: 0.95rem 0 0;
  color: var(--text-muted);
  font-size: 0.86rem;
}

.auth-context-link {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  text-decoration: none;
}

.auth-context-link:hover {
  color: var(--accent);
}

.join-code-toggle {
  margin: -0.15rem 0 1rem;
}

.join-code-panel {
  padding: 0.85rem;
  border: 1px solid rgba(91, 124, 255, 0.18);
  border-radius: 10px;
  background: rgba(91, 124, 255, 0.06);
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dashboard-loading-skeleton {
  min-height: min(620px, calc(100vh - 220px));
}

.dashboard-loading-skeleton :deep(.loading-card:first-of-type) {
  min-height: 178px;
}

.locked-dashboard-mark {
  display: flex;
  justify-content: center;
  padding: clamp(1rem, 4vw, 2rem) 0;
}

.locked-dashboard-mark svg {
  width: clamp(3.4rem, 8vw, 5rem);
  height: clamp(3.4rem, 8vw, 5rem);
  fill: none;
  stroke: var(--text-primary);
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.88;
}

.settings-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 1rem;
  background: rgba(8, 8, 8, 0.32);
  backdrop-filter: blur(10px);
}

.settings-modal {
  width: min(440px, 100%);
  max-height: calc(100dvh - 2rem);
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 26px 80px rgba(8, 8, 8, 0.24);
}

.settings-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0.7rem;
  border-bottom: 1px solid var(--border);
}

.settings-modal-header h2 {
  font-size: 1.28rem;
}

.modal-close-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #ffffff;
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color var(--t-fast), background var(--t-fast), transform var(--t-fast);
}

.modal-close-button:hover {
  transform: translateY(-1px);
  border-color: var(--border-strong);
  background: #f7f7f2;
}

.modal-close-button svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.settings-form {
  display: grid;
  gap: 0.85rem;
  padding: 1rem;
}

.settings-username-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.55rem;
  align-items: stretch;
}

.settings-username-row .form-input.locked {
  color: var(--text-secondary);
  background: #f7f7f2;
  cursor: default;
}

.settings-username-actions {
  display: flex;
  gap: 0.45rem;
}

.settings-inline-button {
  min-width: 58px;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0 0.72rem;
  background: #ffffff;
  color: var(--text-primary);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  transition: background var(--t-fast), border-color var(--t-fast), transform var(--t-fast), opacity var(--t-fast);
}

.settings-inline-button:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: var(--border-strong);
  background: #f7f7f2;
}

.settings-inline-button-primary {
  border-color: transparent;
  background: var(--accent);
  color: #ffffff;
}

.settings-inline-button-primary:hover:not(:disabled) {
  background: #3654ff;
}

.settings-inline-button-danger {
  min-height: 42px;
  border-color: rgba(239, 68, 68, 0.42);
  border-radius: 6px;
  padding: 0 0.95rem;
  background: var(--danger-soft);
  color: #991b1b;
}

.settings-inline-button-danger:hover:not(:disabled) {
  border-color: rgba(239, 68, 68, 0.54);
  background: rgba(239, 68, 68, 0.26);
}

.settings-inline-button:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}

.settings-tier-card,
.settings-upgrade-card {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.85rem;
  background: #fbfbf7;
}

.settings-tier-label {
  display: block;
  color: var(--accent);
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 0.35rem;
}

.settings-tier-card strong {
  display: block;
  font-size: 1.18rem;
  line-height: 1;
  margin-bottom: 0.45rem;
}

.settings-tier-card p,
.settings-upgrade-card p {
  color: var(--text-secondary);
  font-size: 0.92rem;
}

.settings-upgrade-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  flex-wrap: wrap;
  background: linear-gradient(180deg, rgba(91, 124, 255, 0.08), #ffffff);
  border-color: rgba(91, 124, 255, 0.22);
}

.settings-message {
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
  background: rgba(91, 124, 255, 0.1);
  color: var(--text-primary);
  font-size: 0.88rem;
  font-weight: 700;
}

.settings-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
  padding-top: 0.05rem;
}

.spinner {
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #fff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 840px) {
  .dashboard-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .auth-layout {
    grid-template-columns: 1fr;
  }

  .dashboard-hero-actions {
    width: 100%;
    align-items: stretch;
  }

  .settings-button {
    align-self: flex-start;
  }

  .settings-modal-backdrop {
    align-items: center;
    padding: 0.75rem;
  }

  .settings-modal {
    width: 100%;
    max-height: calc(100dvh - 1.5rem);
  }

  .settings-username-row {
    grid-template-columns: 1fr;
  }

  .settings-username-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .settings-inline-button {
    min-height: 40px;
  }

  .settings-modal-actions {
    flex-direction: column-reverse;
  }

  .settings-modal-actions .btn {
    width: 100%;
  }
}
</style>
